import { AddOutlined, CloseOutlined, OpenInNew, SearchOutlined, TuneOutlined } from '@mui/icons-material'
import type { SelectChangeEvent } from '@mui/material'
import {
  Badge,
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import type { MouseEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CustomButton from '@/components/inputs/CustomButton'
import { LOCAL_PQ_PLAYBOOK_URL, PrequalificationRouteInformation } from '@/features/prequalification/constants'
import LocalPqResourceSetsFiltersPopover from '@/features/prequalification/localControl/components/LocalPqResourceSetsFiltersPopover'
import { getPhaseChipProps } from '@/features/prequalification/localControl/utils/phasePresentation'
import type { ResourceSetFilters } from '@/features/prequalification/localControl/utils/resourceSetFilters'
import { activePopoverFilterCount } from '@/features/prequalification/localControl/utils/resourceSetFilters'
import type { PqPhase } from '@/features/prequalification/types'

type StatusValue = PqPhase | 'all'

const STATUS_PHASES: PqPhase[] = ['SCHEDULED', 'ENQUEUED', 'STARTED', 'PASSED', 'FAILED', 'CANCELLED']

const CONTROL_HEIGHT = 40

type LocalPqResourceSetsToolbarProps = {
  filters: ResourceSetFilters
  onFiltersChange: (filters: ResourceSetFilters) => void
}

/**
 * The bar above the resource sets table: free-text search and a status filter on the
 * left, the extra-filters popover trigger, and the playbook link plus the primary
 * "New resource set" action pinned to the right. Search and status apply live; the
 * rest live in the popover.
 */
function LocalPqResourceSetsToolbar({ filters, onFiltersChange }: Readonly<LocalPqResourceSetsToolbarProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null)

  const popoverCount = activePopoverFilterCount(filters)

  const handleStatusChange = (event: SelectChangeEvent<StatusValue>) =>
    onFiltersChange({ ...filters, status: event.target.value as StatusValue })

  const renderStatusValue = (value: StatusValue) => {
    if (value === 'all') return t('prequalifications.list.filters.status_all')

    return getPhaseChipProps(value, t).label
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, flex: '1 1 auto' }}>
        <TextField
          placeholder={t('prequalifications.list.filters.search_placeholder')}
          size="small"
          slotProps={{
            htmlInput: { 'aria-label': t('prequalifications.list.filters.search_label') },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: filters.search ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('common.clear')}
                    edge="end"
                    size="small"
                    onClick={() => onFiltersChange({ ...filters, search: '' })}
                  >
                    <CloseOutlined fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
          sx={{ flex: '1 1 260px', maxWidth: 460, '& .MuiOutlinedInput-root': { height: CONTROL_HEIGHT } }}
          value={filters.search}
          onChange={(event) => onFiltersChange({ ...filters, search: event.target.value })}
        />

        <FormControl size="small" sx={{ minWidth: 168 }}>
          <Select<StatusValue>
            displayEmpty
            MenuProps={{
              sx: {
                '& .MuiMenuItem-root': { fontSize: '0.875rem' },
                '& .MuiMenuItem-root .MuiSvgIcon-root': { fontSize: '1.25rem', color: 'text.secondary' },
              },
            }}
            aria-label={t('prequalifications.list.filters.status_label')}
            renderValue={renderStatusValue}
            sx={{ height: CONTROL_HEIGHT, '& .MuiSelect-select': { fontSize: '0.875rem' } }}
            value={filters.status}
            onChange={handleStatusChange}
          >
            <MenuItem value="all">{t('prequalifications.list.filters.status_all')}</MenuItem>
            {STATUS_PHASES.map((phase) => {
              const { label, icon } = getPhaseChipProps(phase, t)
              return (
                <MenuItem key={phase} sx={{ gap: 1 }} value={phase}>
                  {icon}
                  {label}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>

        <Badge badgeContent={popoverCount} color="primary" overlap="rectangular">
          <CustomButton
            aria-haspopup="dialog"
            color="primary"
            startIcon={<TuneOutlined />}
            sx={{ height: CONTROL_HEIGHT }}
            variant="outlined"
            onClick={(event: MouseEvent<HTMLButtonElement>) => setFiltersAnchor(event.currentTarget)}
          >
            {t('prequalifications.list.filters.button')}
          </CustomButton>
        </Badge>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
        <Link
          href={LOCAL_PQ_PLAYBOOK_URL}
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'underline' }}
          target="_blank"
        >
          <OpenInNew />
          {t('prequalifications.list.playbook.toolbar_button')}
        </Link>

        <Tooltip title={t('prequalifications.list.new_resource_set')}>
          <span>
            <CustomButton
              color="primary"
              startIcon={<AddOutlined />}
              sx={{ height: CONTROL_HEIGHT }}
              variant="contained"
              onClick={() => navigate(PrequalificationRouteInformation.NEW_RESOURCE_SET.navigationPath())}
            >
              {t('prequalifications.list.new_resource_set')}
            </CustomButton>
          </span>
        </Tooltip>
      </Box>

      <LocalPqResourceSetsFiltersPopover
        anchorEl={filtersAnchor}
        filters={filters}
        open={filtersAnchor !== null}
        onClose={() => setFiltersAnchor(null)}
        onFiltersChange={onFiltersChange}
      />
    </Box>
  )
}

export default LocalPqResourceSetsToolbar
